/* Gripzilla order tracking page. Talks to the gripzilla-track Worker (see /wismo/worker). */
(function () {
  const root = document.querySelector("[data-gz-track]");
  if (!root) return;

  const cfg = {
    endpoint: root.dataset.endpoint,
    supportUrl: root.dataset.supportUrl || "/pages/contact-us",
    guideUrl: root.dataset.guideUrl || "",
    showEta: root.dataset.showEta === "true",
  };

  const $ = (sel) => root.querySelector(sel);
  const form = $("[data-gz-form]");
  const btn = $("[data-gz-submit]");
  const errorBox = $("[data-gz-error]");
  const result = $("[data-gz-result]");
  const formCard = $("[data-gz-form-card]");

  const STEPS = [
    ["placed", "Placed"],
    ["packing", "Packing"],
    ["shipped", "Shipped"],
    ["in_transit", "On the way"],
    ["out_for_delivery", "Out today"],
    ["delivered", "Delivered"],
  ];

  // Customer-facing copy per stage. No em dashes, never promise a date (brand rules).
  const COPY = {
    placed: {
      tag: "Order received",
      title: "We've got it",
      body: "Your order is in and queued for our warehouse. You'll get an email with tracking the moment it ships.",
    },
    packing: {
      tag: "At the warehouse",
      title: "Being packed",
      body: "Our warehouse is picking and packing your order right now. Tracking shows up here as soon as the label is printed.",
    },
    shipped: {
      tag: "Label created",
      title: "Packed and labeled",
      body: "Your order is boxed and has a shipping label. Carrier updates can take a day or two to appear after the label is created, so don't worry if the timeline below is quiet.",
    },
    in_transit: {
      tag: "In transit",
      title: "On the way",
      body: (d) =>
        d.order.international
          ? "Your order is moving through the carrier network. International delivery normally takes 5 to 20 business days and depends a lot on the destination. Customs can add time."
          : "Your order is moving through the carrier network. US orders normally arrive 2 to 5 business days after they ship.",
    },
    out_for_delivery: {
      tag: "Out for delivery",
      title: "Out for delivery",
      body: "It's on the truck. Keep an eye on the door today.",
    },
    delivered: {
      tag: "Delivered",
      title: "Delivered",
      body: () =>
        "Your order has arrived. Time to train." +
        (cfg.guideUrl ? ` <a href="${esc(cfg.guideUrl)}">Open the setup and workout guide</a>.` : ""),
    },
    attention: {
      tag: "Needs a look",
      tone: "alert",
      title: "Carrier update",
      body: () =>
        `The carrier flagged something on this shipment, usually a missed delivery or a short delay. Check the carrier link below. If it hasn't cleared up within a day, <a href="${esc(cfg.supportUrl)}">contact us</a> with your order number and we'll sort it.`,
    },
    cancelled: {
      tag: "Cancelled",
      tone: "alert",
      title: "Order cancelled",
      body: () => `This order was cancelled. If that's unexpected, <a href="${esc(cfg.supportUrl)}">get in touch</a> and we'll look into it.`,
    },
  };

  // Prefill order number from ?order= (e.g. a link in the shipping email). Never put the email in a URL.
  const params = new URLSearchParams(location.search);
  if (params.get("order")) form.order.value = params.get("order");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const order = form.order.value.trim();
    const email = form.email.value.trim();
    hideError();
    if (!order || !email) return showError("Enter your order number and the email you used at checkout.");

    setLoading(true);
    try {
      const data = await lookup(order, email);
      render(data);
    } catch (err) {
      if (err.status === 404)
        showError("We couldn't match that order number and email. Check both against your confirmation email. The order number starts with #.");
      else if (err.status === 429) showError("Too many lookups in a row. Give it a minute and try again.");
      else showError(`Something went wrong on our end. Try again shortly, or <a href="${esc(cfg.supportUrl)}">contact us</a>.`);
    } finally {
      setLoading(false);
    }
  });

  async function lookup(order, email) {
    if (window.GZ_TRACK_DEMO) return window.GZ_TRACK_DEMO(order, email);
    const r = await fetch(cfg.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order, email }),
    });
    if (!r.ok) throw Object.assign(new Error("lookup failed"), { status: r.status });
    return r.json();
  }

  /* ---------------- render ---------------- */

  function render(d) {
    const copy = COPY[d.stage] || COPY.placed;
    const body = typeof copy.body === "function" ? copy.body(d) : copy.body;
    const stageIdx = STEPS.findIndex(([k]) => k === d.stage);
    // "attention" sits wherever the furthest shipment got to; show it at in-transit.
    const progressIdx = d.stage === "attention" ? 3 : stageIdx;

    let banner = "";
    if (d.warehouse?.onHold)
      banner = "Heads up: our warehouse paused this order for a quick check. We're on it and will email you if we need anything.";
    else if (d.warehouse?.delayed && STEPS.findIndex(([k]) => k === d.stage) < 2)
      banner = "Heads up: your order is taking a little longer than usual at the warehouse. No action needed, it's in the queue.";
    if (d.partial) banner = (banner ? banner + " " : "") + "Your order is shipping in more than one box. Each one is tracked below.";

    result.innerHTML = `
      <section class="gz-card gz-status" aria-live="polite">
        <div class="gz-status-top">
          <span>Order <strong>${esc(d.order.name)}</strong></span>
          <span>Placed <strong>${fmtDate(d.order.placedAt)}</strong></span>
          ${d.order.shipTo ? `<span>Shipping to <strong>${esc(d.order.shipTo)}</strong></span>` : ""}
        </div>
        <div class="gz-status-main">
          <span class="gz-stage-tag" data-tone="${copy.tone || ""}">${copy.tag}</span>
          <h2 class="gz-stage-title">${copy.title}</h2>
          <p class="gz-stage-copy">${body}</p>
        </div>
        ${banner ? `<div class="gz-banner">${esc(banner)}</div>` : ""}
        ${d.stage === "cancelled" ? "<div style='height:24px'></div>" : progressHtml(progressIdx)}
      </section>

      ${d.shipments
        .map((s, i) => shipmentHtml(s, i, d.shipments.length))
        .join("")}

      <section class="gz-card">
        <h3 class="gz-section-title">In this order</h3>
        <ul class="gz-items">${d.order.items.map(itemHtml).join("")}</ul>
      </section>

      <div class="gz-again"><button type="button" data-gz-again>Track a different order</button></div>
    `;

    formCard.classList.add("gz-hidden");
    result.classList.remove("gz-hidden");

    requestAnimationFrame(() => {
      const fill = result.querySelector(".gz-progress-fill");
      if (fill) fill.style.width = `calc((100% - var(--gz-pad, 48px)) * ${Math.max(progressIdx, 0) / 6})`;
    });

    result.querySelectorAll("[data-gz-copy]").forEach((b) =>
      b.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(b.dataset.gzCopy);
          b.textContent = "Copied";
          setTimeout(() => (b.textContent = "Copy"), 1500);
        } catch {}
      })
    );
    result.querySelectorAll("[data-gz-more]").forEach((b) =>
      b.addEventListener("click", () => {
        b.parentElement.previousElementSibling.querySelectorAll(".gz-hidden").forEach((n) => n.classList.remove("gz-hidden"));
        b.parentElement.remove();
      })
    );
    result.querySelector("[data-gz-again]").addEventListener("click", () => {
      result.classList.add("gz-hidden");
      result.innerHTML = "";
      formCard.classList.remove("gz-hidden");
      form.order.focus();
    });

    result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function progressHtml(idx) {
    const pad = window.matchMedia("(max-width: 720px)").matches ? "16px" : "48px";
    return `
      <ol class="gz-progress" style="--gz-pad:${pad}" aria-label="Order progress">
        <span class="gz-progress-fill" aria-hidden="true"></span>
        ${STEPS.map(([key, label], i) => {
          const state = i < idx ? "is-done" : i === idx ? "is-current" : "";
          return `<li class="gz-step ${state}" ${i === idx ? 'aria-current="step"' : ""}>
            <span class="gz-dot" aria-hidden="true">${i < idx ? "&#10003;" : i + 1}</span>
            <span class="gz-step-label">${label}</span>
          </li>`;
        }).join("")}
      </ol>`;
  }

  function shipmentHtml(s, i, total) {
    const visible = 4;
    const events = s.events || [];
    const eta =
      cfg.showEta && s.carrierEstimate
        ? `<div class="gz-eta"><strong>Carrier estimate:</strong> ${fmtDate(s.carrierEstimate)} <small>(set by the carrier and can change)</small></div>`
        : "";
    const delivered = s.deliveredAt ? `<div class="gz-eta"><strong>Delivered</strong> ${fmtDateTime(s.deliveredAt)}</div>` : "";
    const items =
      total > 1 && s.items?.length
        ? `<p class="gz-ship-items">${s.items.map((it) => `${it.quantity} &times; ${esc(it.title)}`).join(", ")}</p>`
        : "";

    return `
      <section class="gz-card">
        <h3 class="gz-section-title"><span>${total > 1 ? `Package ${i + 1} of ${total}` : "Tracking"}</span><span>${stepLabel(s.stage)}</span></h3>
        <div class="gz-ship-head">
          <div>
            <div class="gz-carrier">${esc(s.carrier || "Carrier pending")}</div>
            ${
              s.number
                ? `<div class="gz-tracking-no">${esc(s.number)} <button type="button" class="gz-copy" data-gz-copy="${esc(s.number)}">Copy</button></div>`
                : ""
            }
          </div>
          ${s.url ? `<a class="gz-btn gz-btn--outline" href="${esc(s.url)}" target="_blank" rel="noopener">Carrier tracking &#8599;</a>` : ""}
        </div>
        ${items}${delivered || eta}
        <ol class="gz-timeline">
          ${events
            .map(
              (e, j) => `<li class="gz-event ${j >= visible ? "gz-hidden" : ""}">
                <span class="gz-event-mark" aria-hidden="true"></span>
                <div>
                  <div class="gz-event-label">${esc(e.label)}</div>
                  <div class="gz-event-meta">${fmtDateTime(e.at)}${e.location ? ` &middot; ${esc(e.location)}` : ""}</div>
                  ${e.detail && e.detail !== e.label ? `<div class="gz-event-detail">${esc(e.detail)}</div>` : ""}
                </div>
              </li>`
            )
            .join("")}
          ${
            s.awaitingFirstScan
              ? `<li class="gz-event"><span class="gz-event-mark" aria-hidden="true" style="border-style:dashed"></span><div><div class="gz-event-label">Waiting for first carrier scan</div><div class="gz-event-meta">Updates appear once the carrier picks it up</div></div></li>`
              : ""
          }
        </ol>
        ${events.length > visible ? `<div class="gz-timeline-more"><button type="button" class="gz-copy" data-gz-more>Show all ${events.length} updates</button></div>` : ""}
      </section>`;
  }

  function itemHtml(it) {
    return `<li class="gz-item">
      ${it.image ? `<img src="${esc(it.image)}" alt="" loading="lazy" width="64" height="64">` : `<span class="gz-item-ph"></span>`}
      <div><div class="gz-item-title">${esc(it.title)}</div>${it.variant ? `<div class="gz-item-variant">${esc(it.variant)}</div>` : ""}</div>
      <div class="gz-item-qty">&times;${it.quantity}</div>
    </li>`;
  }

  /* ---------------- utils ---------------- */

  function stepLabel(stage) {
    if (stage === "attention") return "Needs a look";
    return (STEPS.find(([k]) => k === stage) || [, ""])[1];
  }
  function setLoading(on) {
    btn.disabled = on;
    btn.textContent = on ? "Finding your order" : btn.dataset.label;
    $("[data-gz-loading]").classList.toggle("gz-hidden", !on);
  }
  function showError(html) {
    errorBox.innerHTML = html;
    errorBox.classList.remove("gz-hidden");
  }
  function hideError() {
    errorBox.classList.add("gz-hidden");
  }
  function fmtDate(iso) {
    return iso ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso)) : "";
  }
  function fmtDateTime(iso) {
    return iso
      ? new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(iso))
      : "";
  }
  function esc(s) {
    return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
})();
